// One filter group — Polish B2 / v1 layout.
//
// Bordered card with N condition rows. Each row's leftmost cell is
// the group's operator gutter:
//   - first row → "Where" plain label
//   - subsequent rows → AND / OR pill (clicking any pill toggles
//     the group's ``groupOperator`` for the whole group)
// Delete affordances: per-condition trash + a group-level trash
// (hidden for the only group). "+ Add nested filter" inside the
// card adds another condition.

import { Plus, Trash2 } from "lucide-react";

import { FilterConditionRow } from "./FilterCondition";
import {
  type FilterCondition,
  type FilterGroup as FilterGroupState,
  makeId,
} from "./treeCodec";


export interface FilterGroupProps {
  group: FilterGroupState;
  onChange: (next: FilterGroupState) => void;
  onRemove: () => void;
  /** Hides the group-delete affordance for the only group. */
  canRemove: boolean;
}


export function FilterGroupCard({
  group, onChange, onRemove, canRemove,
}: FilterGroupProps) {
  function toggleOperator(): void {
    onChange({
      ...group,
      groupOperator: group.groupOperator === "AND" ? "OR" : "AND",
    });
  }
  function updateCondition(
    next: FilterCondition,
  ): void {
    onChange({
      ...group,
      conditions: group.conditions.map((c) =>
        c.id === next.id ? next : c,
      ),
    });
  }
  function removeCondition(id: string): void {
    onChange({
      ...group,
      conditions: group.conditions.filter((c) => c.id !== id),
    });
  }
  function addCondition(): void {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        {
          id: makeId(),
          field: "",
          operator: "is",
          value: "",
        },
      ],
    });
  }
  return (
    <section
      data-testid={`filter-group-${group.id}`}
      className="relative flex flex-col gap-3 rounded-md border border-border-subtle bg-recessed/40 p-3"
    >
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove group"
          data-testid={`filter-group-remove-${group.id}`}
          className="absolute right-2 top-2 rounded p-1 text-ember/70 hover:text-ember"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      )}
      <div className="flex flex-col gap-3">
        {group.conditions.length === 0 && (
          <p className="text-[11px] italic text-text-tertiary">
            No conditions yet — click "Add nested filter" below.
          </p>
        )}
        {group.conditions.map((c, idx) => (
          <div key={c.id} className="flex items-center gap-3">
            <div className="w-16 flex-shrink-0">
              {idx === 0 ? (
                <span
                  className="text-[11px] font-semibold text-text-primary"
                >
                  Where
                </span>
              ) : (
                <button
                  type="button"
                  data-testid={`filter-group-op-${group.id}`}
                  onClick={toggleOperator}
                  className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-[11px] font-medium text-text-primary hover:bg-recessed"
                >
                  {group.groupOperator}
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <FilterConditionRow
                condition={c}
                onChange={updateCondition}
                onRemove={() => removeCondition(c.id)}
              />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addCondition}
        data-testid={`filter-group-add-condition-${group.id}`}
        className="inline-flex w-fit items-center gap-1 text-[11px] font-medium text-teal hover:text-text-primary"
      >
        <Plus size={12} aria-hidden="true" />
        Add nested filter
      </button>
    </section>
  );
}
